<template>
  <div class="flex flex-col min-h-0">
    <!-- Empty -->
    <div
      v-if="!items.length"
      class="flex flex-col items-center justify-center py-12 text-center border border-dashed rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40"
    >
      <span
        class="flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400"
      >
        <i class="text-2xl mdi mdi-comment-outline"></i>
      </span>
      <p class="text-sm font-medium text-slate-600 dark:text-slate-300">
        {{ emptyLabel }}
      </p>
      <p class="mt-1 text-[11px] text-slate-400 max-w-xs">{{ emptyHint }}</p>
    </div>

    <template v-else>
      <!-- Toolbar -->
      <div
        class="flex flex-wrap items-center justify-between gap-2 pb-2.5 mb-3 border-b shrink-0 border-slate-200/70 dark:border-slate-700/70"
      >
        <div class="flex flex-wrap items-center gap-2">
          <h3
            class="text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
          >
            {{ title }}
          </h3>
          <span
            class="inline-flex items-center justify-center px-2 text-[10px] font-semibold rounded-lg h-5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
          >
            {{ items.length }}
          </span>
          <span
            v-if="participantCount > 1"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-[10px] text-slate-600 dark:text-slate-300"
          >
            <i class="mdi mdi-account-group-outline text-[13px]"></i>
            {{ participantsLabel }} {{ participantCount }}
          </span>
          <span
            v-if="lastActivityLabel"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-primary/10 text-[10px] font-medium text-primary"
          >
            <span class="relative flex w-1.5 h-1.5">
              <span
                class="absolute inline-flex w-full h-full rounded-full opacity-75 bg-primary animate-ping"
              ></span>
              <span
                class="relative inline-flex w-1.5 h-1.5 rounded-full bg-primary"
              ></span>
            </span>
            {{ lastActivityLabel }}
          </span>
        </div>

        <button
          type="button"
          class="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium border rounded-lg border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 bg-white/80 dark:bg-slate-900/60 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          @click="newestFirst = !newestFirst"
        >
          <i
            class="mdi text-[14px]"
            :class="
              newestFirst
                ? 'mdi-sort-clock-descending-outline'
                : 'mdi-sort-clock-ascending-outline'
            "
          ></i>
          {{ newestFirst ? sortNewestLabel : sortOldestLabel }}
        </button>
      </div>

      <!-- Thread -->
      <div
        ref="scrollRef"
        class="relative flex-1 min-h-0 overflow-y-auto custom-scrollbar"
        :class="scrollClass"
      >
        <ol class="pb-1 space-y-2.5">
          <template v-for="row in rows" :key="row.key">
            <!-- Day separator -->
            <li v-if="row.type === 'day'" class="flex items-center gap-3">
              <span
                class="flex items-center justify-center rounded-full w-9 h-9 text-slate-400"
              >
                <i class="mdi mdi-comment-text-multiple-outline text-[15px]"></i>
              </span>
              <span
                class="px-2.5 py-0.5 text-[10px] font-semibold rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
              >
                {{ row.label }}
              </span>
            </li>

            <!-- Comment -->
            <li v-else class="flex gap-3">
              <div class="shrink-0">
                <span
                  class="flex items-center justify-center w-9 h-9 text-[11px] font-bold rounded-full"
                  :class="avatarClass(row.item)"
                  :title="displayName(row.item) || roleLabel(row.item)"
                >
                  <template v-if="initials(row.item)">
                    {{ initials(row.item) }}
                  </template>
                  <i v-else class="mdi mdi-account text-[16px]"></i>
                </span>
              </div>

              <article
                class="flex-1 min-w-0 p-3.5 border shadow-sm rounded-xl transition-colors hover:border-primary/30"
                :class="cardClass(row)"
              >
                <header class="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span
                    v-if="displayName(row.item)"
                    class="text-[13px] font-semibold text-slate-800 dark:text-slate-100 truncate max-w-[11rem]"
                  >
                    {{ displayName(row.item) }}
                  </span>

                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-lg shrink-0"
                    :class="roleBadgeClass(row.item)"
                  >
                    <i class="mdi text-[12px]" :class="roleIcon(row.item)"></i>
                    {{ roleLabel(row.item) }}
                  </span>

                  <span
                    v-if="row.isLatest"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-lg shrink-0 bg-primary/10 text-primary"
                  >
                    {{ latestLabel }}
                  </span>

                  <span
                    class="flex items-center gap-2 text-[10px] text-slate-400 tabular-nums"
                    :class="lang === 'ar' ? 'mr-auto' : 'ml-auto'"
                  >
                    <time
                      :datetime="row.item.created_at"
                      :title="absoluteTime(row.item.created_at)"
                    >
                      {{ relativeTime(row.item.created_at) }}
                    </time>
                    <span class="text-slate-300 dark:text-slate-600">
                      #{{ row.number }}
                    </span>
                  </span>
                </header>

                <p
                  v-if="commentText(row.item)"
                  class="mt-1.5 text-[13px] leading-relaxed break-words whitespace-pre-wrap text-slate-700 dark:text-slate-200"
                >
                  {{ commentText(row.item) }}
                </p>
                <p v-else class="mt-1.5 text-[12px] italic text-slate-400">
                  {{ attachmentsOnlyLabel }}
                </p>

                <div
                  v-if="row.item.attachments?.length"
                  class="pt-2.5 mt-2.5 border-t border-slate-200/70 dark:border-slate-700/70"
                >
                  <TicketAttachmentGrid
                    :attachments="row.item.attachments"
                    :lang="lang"
                    :title="attachmentsLabel"
                    compact
                  />
                </div>
              </article>
            </li>
          </template>
        </ol>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import {
  commentAuthorId,
  commentAuthorName,
  formatTicketDateTime,
  formatTicketDayDate,
  formatTicketRelativeTime,
  getCommentSenderKind,
  ticketDayKey,
  ticketDayOffset,
} from "@/utils/ticket";
import TicketAttachmentGrid from "./TicketAttachmentGrid.vue";

const props = defineProps({
  items: { type: Array, default: () => [] },
  lang: { type: String, default: "ar" },
  t: { type: Function, default: null },
  title: { type: String, default: "Comments" },
  emptyLabel: { type: String, default: "No comments yet." },
  emptyHint: { type: String, default: "" },
  legendCreator: { type: String, default: "" },
  legendHandler: { type: String, default: "" },
  ticketCreator: { type: Object, default: null },
  ticketHandler: { type: Object, default: null },
  currentUserId: { type: [Number, String], default: null },
  scrollClass: { type: String, default: "max-h-[min(520px,58vh)]" },
});

/**
 * Tints are drawn from the brand palette used by the page badges so multiple
 * participants stay recognisable without introducing off-theme hues.
 */
const AVATAR_PALETTE = [
  "bg-primary/10 text-primary",
  "bg-secondary/20 text-secondary",
  "bg-primary-dark/10 text-primary-dark dark:bg-primary-dark/40 dark:text-primary-light",
  "bg-secondary-dark/20 text-secondary-dark",
  "bg-primary/20 text-primary-dark dark:text-primary-light",
  "bg-secondary-light/25 text-secondary-dark",
  "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
];

const newestFirst = ref(false);
const scrollRef = ref(null);

/** Keeps the freshest comment in view, which sits at whichever end sorting puts it. */
async function scrollToNewest(behavior = "smooth") {
  await nextTick();
  const el = scrollRef.value;
  if (!el) return;
  el.scrollTo({ top: newestFirst.value ? 0 : el.scrollHeight, behavior });
}

watch(
  () => props.items.length,
  (count, previous) => {
    if (count > (previous || 0)) {
      scrollToNewest(previous ? "smooth" : "auto");
    }
  },
);

watch(newestFirst, () => scrollToNewest("auto"));

onMounted(() => scrollToNewest("auto"));

function tr(key, ar, en) {
  const translated = props.t?.(key);
  if (translated && translated !== key) return translated;
  return props.lang === "ar" ? ar : en;
}

const emptyHint = computed(
  () =>
    props.emptyHint ||
    (props.lang === "ar"
      ? "أضف تعليقك أو مرفقات في النموذج أدناه"
      : "Add your comment or files in the form below"),
);

const youLabel = computed(() => tr("ticket_comment_you", "أنت", "You"));

const participantsLabel = computed(() =>
  tr("ticket_comment_participants", "المشاركون", "Participants"),
);

const sortOldestLabel = computed(() =>
  tr("ticket_comments_sort_oldest", "الأقدم أولاً", "Oldest first"),
);

const sortNewestLabel = computed(() =>
  tr("ticket_comments_sort_newest", "الأحدث أولاً", "Newest first"),
);

const attachmentsLabel = computed(() =>
  tr("ticket_attachments", "المرفقات", "Attachments"),
);

const attachmentsOnlyLabel = computed(() =>
  tr("ticket_comment_attachments_only", "مرفقات بدون نص", "Attachments only"),
);

const latestLabel = computed(() => tr("ticket_event_latest", "الأحدث", "Latest"));
const todayLabel = computed(() => tr("ticket_today", "اليوم", "Today"));
const yesterdayLabel = computed(() => tr("ticket_yesterday", "أمس", "Yesterday"));

const lastActivityLabel = computed(() => {
  const newest = chronological.value[chronological.value.length - 1];
  if (!newest) return "";
  return formatTicketRelativeTime(newest.created_at, props.lang);
});

/**
 * Oldest first, regardless of the order the API sends. Day grouping and the
 * "latest" marker both rely on entries being contiguous in time, and a newly
 * posted comment is appended to whatever the list already held.
 * Timestamps are parsed once instead of inside the comparator.
 */
const chronological = computed(() =>
  props.items
    .map((item, index) => {
      const time = Date.parse(item?.created_at);
      return { item, index, time: Number.isNaN(time) ? 0 : time };
    })
    .sort((a, b) => a.time - b.time || a.index - b.index)
    .map((entry) => entry.item),
);

/** Comments in display order, interleaved with day separators. */
const rows = computed(() => {
  const items = chronological.value;
  const total = items.length;
  const ordered = items.map((item, index) => ({
    type: "comment",
    item,
    number: index + 1,
    isLatest: total > 1 && index === total - 1,
    key: item.id ?? `comment-${index}`,
  }));

  const sequence = newestFirst.value ? [...ordered].reverse() : ordered;

  const out = [];
  let currentDay = null;
  sequence.forEach((row) => {
    const key = ticketDayKey(row.item.created_at);
    if (key !== currentDay) {
      currentDay = key;
      out.push({
        type: "day",
        key: `day-${key}`,
        label: dayLabel(row.item.created_at),
      });
    }
    out.push(row);
  });

  return out;
});

function dayLabel(value) {
  const offset = ticketDayOffset(value);
  if (offset === 0) return todayLabel.value;
  if (offset === 1) return yesterdayLabel.value;
  return formatTicketDayDate(value, props.lang);
}

const participantCount = computed(
  () => new Set(props.items.map((item) => authorKey(item))).size,
);

function commentText(item) {
  return String(item.comment || item.body || item.content || "").trim();
}

const supportTeamLabel = computed(() =>
  props.t?.("ticket_support_team") ||
    (props.lang === "ar" ? "فريق الدعم" : "Support team"),
);

/**
 * Prefer the comment's own sender fields. For staff replies with no name, show a
 * generic support label instead of inferring from the current assignee.
 */
function senderName(item) {
  const name = commentAuthorName(item);
  if (name) return name;
  if (isCreator(item)) {
    return String(props.ticketCreator?.name || "").trim();
  }
  return supportTeamLabel.value;
}

/** Own comments are titled "You"; everyone else is titled by their name. */
function displayName(item) {
  return isMine(item) ? youLabel.value : senderName(item);
}

function authorKey(item) {
  const id = commentAuthorId(item);
  if (id != null) return `id:${id}`;
  const name = senderName(item);
  return name ? `name:${name.toLowerCase()}` : `role:${isCreator(item)}`;
}

function initials(item) {
  const name = senderName(item);
  if (!name) return "";
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((part) => part.charAt(0).toUpperCase()).join("");
}

function isMine(item) {
  const id = commentAuthorId(item);
  if (id != null && props.currentUserId != null) {
    return String(id) === String(props.currentUserId);
  }
  return Boolean(item.is_mine || item.isMine || item.mine);
}

function isCreator(item) {
  return (
    getCommentSenderKind(item, props.ticketCreator, props.ticketHandler) ===
    "creator"
  );
}

function absoluteTime(value) {
  return formatTicketDateTime(value, props.lang);
}

function relativeTime(value) {
  return formatTicketRelativeTime(value, props.lang);
}

function roleLabel(item) {
  if (isCreator(item)) {
    return props.legendCreator || (props.lang === "ar" ? "مقدّم التذكرة" : "Ticket creator");
  }
  return props.legendHandler || (props.lang === "ar" ? "مسؤول التذكرة" : "Ticket handler");
}

function roleIcon(item) {
  return isCreator(item) ? "mdi-account-outline" : "mdi-headset";
}

function roleBadgeClass(item) {
  return isCreator(item)
    ? "bg-primary/10 text-primary"
    : "bg-secondary/15 text-secondary";
}

function avatarClass(item) {
  const key = authorKey(item);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) % 997;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

/** Own comments use the same soft teal wash as other "owner" panels in the app. */
function cardClass(row) {
  const surface = isMine(row.item)
    ? "bg-[#f3faf9] dark:bg-slate-900/40"
    : "bg-white dark:bg-slate-900/60";

  const edge = row.isLatest
    ? "border-primary/25 ring-1 ring-primary/10"
    : "border-slate-200 dark:border-slate-700";

  return `${surface} ${edge}`;
}
</script>
